/**
 * One caller-owned sign-in-security surface for every Marble host (#2761).
 *
 * GoTrue remains the only factor/session owner. This composition holds the
 * one-time TOTP material only while its setup frame is mounted, projects only
 * the package's safe MfaFactor shape, and uses the AuthProvider's injected
 * client rather than the environment singleton.
 */
import {
  useCallback, useEffect, useRef, useState, type ReactNode, type RefObject,
} from 'react'
import {
  Button, Choice, CopyField, EmptyState, ErrorState, FactList, Field, Input, Modal,
  Notice, PageSection, Pill, Surface, useMessage,
} from '@solum/ui'
import { assuranceOf } from './assurance.js'
import type { MfaFactor, TotpEnrollment } from './mfa.js'
import type { RecoveryCodeReveal, RecoveryCodeStatus } from './recovery-codes.js'
import { useAuth } from './react.js'

export interface SignInSecurityIdentity {
  email: string | null
  custody: 'local' | 'directory'
}

export function signInIdentityCustody(idp: unknown): SignInSecurityIdentity['custody'] {
  return idp == null || idp === 'local' || idp === 'self' || idp === 'auto_signup'
    ? 'local'
    : 'directory'
}

export interface SignInSecuritySettingsProps {
  identity: SignInSecurityIdentity
  onClose: () => void
  restoreFocusTo?: RefObject<HTMLElement | null>
  recoveryRedirectTo: string
  /** Host-owned governed security sections that belong in this one account surface. */
  additionalSections?: ReactNode
}

type LoadState = 'loading' | 'ready' | 'error'
type ResetState = 'idle' | 'sending' | 'sent' | 'failed'
type RecoveryAction = 'generate' | 'regenerate' | 'delete' | 'verify'

const boundedFailure = (value: unknown, fallback: string) =>
  value instanceof Error && value.message.trim() ? value.message : fallback

const qrImage = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

export function SignInSecuritySettings({
  identity, onClose, restoreFocusTo, recoveryRedirectTo, additionalSections,
}: SignInSecuritySettingsProps) {
  const { client, session } = useAuth()
  const [factors, setFactors] = useState<MfaFactor[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [failure, setFailure] = useState<string | null>(null)
  const [resetState, setResetState] = useState<ResetState>('idle')
  const [name, setName] = useState('')
  const [otp, setOtp] = useState('')
  const [enrolling, setEnrolling] = useState(false)
  const [enrollment, setEnrollment] = useState<TotpEnrollment | null>(null)
  const [starting, setStarting] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [cleaning, setCleaning] = useState(false)
  const [confirming, setConfirming] = useState<MfaFactor | null>(null)
  const [removing, setRemoving] = useState(false)
  const [success, setSuccess] = useState('')
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryCodeStatus | null>(null)
  const [recoveryLoadState, setRecoveryLoadState] = useState<LoadState>('loading')
  const [recoveryReveal, setRecoveryReveal] = useState<RecoveryCodeReveal | null>(null)
  const [recoveryCode, setRecoveryCode] = useState('')
  const [recoveryAcknowledged, setRecoveryAcknowledged] = useState(false)
  const [recoveryAction, setRecoveryAction] = useState<RecoveryAction | null>(null)
  const [confirmRecovery, setConfirmRecovery] = useState<'regenerate' | 'delete' | null>(null)
  const enrollmentRef = useRef<TotpEnrollment | null>(null)
  const recoveryRevealRef = useRef<RecoveryCodeReveal | null>(null)
  const removalInFlight = useRef(false)
  const startInFlight = useRef(false)
  const verifyInFlight = useRef(false)
  const cleanupInFlight = useRef(false)
  const resetInFlight = useRef(false)
  const recoveryInFlight = useRef(false)
  const verificationUnmountCleanup = useRef<TotpEnrollment | null>(null)
  const mounted = useRef(true)
  const nameInput = useRef<HTMLInputElement>(null)
  const otpInput = useRef<HTMLInputElement>(null)
  const addButton = useRef<HTMLButtonElement>(null)
  const factorsRoot = useRef<HTMLDivElement>(null)

  const msg = {
    title: useMessage('auth.security.title', 'Sign-in and security'),
    localSummary: useMessage('auth.security.summary.local', 'Manage how you sign in and your authenticator apps.'),
    directorySummary: useMessage('auth.security.summary.directory', 'Your organisation’s directory owns sign-in changes. You can still manage your Marble authenticator apps here.'),
    close: useMessage('auth.security.action.close', 'Close'),
    signedInAs: useMessage('auth.security.fact.signed_in_as', 'Signed in as'),
    method: useMessage('auth.security.fact.method', 'Signed in with'),
    session: useMessage('auth.security.fact.session', 'This session'),
    password: useMessage('auth.security.method.password', 'Your email and password'),
    directory: useMessage('auth.security.method.directory', 'Your organisation’s directory'),
    aal2: useMessage('auth.security.session.aal2', 'Two-factor authentication'),
    aal1: useMessage('auth.security.session.aal1', 'A single factor'),
    reset: useMessage('auth.security.reset.send', 'Send a password reset link'),
    resetSending: useMessage('auth.security.reset.sending', 'Sending…'),
    resetSent: useMessage('auth.security.reset.sent', 'If this account can receive a reset link, it is on the way to {{email}}.', { email: identity.email ?? '' }),
    resetFailed: useMessage('auth.security.reset.failed', 'The reset request could not be completed. Please try again.'),
    factorsTitle: useMessage('auth.security.factors.title', 'Authenticator apps'),
    factorsSummary: useMessage('auth.security.factors.summary', 'These are enrolled on your account. They are separate from the assurance level of this session.'),
    loading: useMessage('auth.security.factors.loading', 'Loading your authenticator apps…'),
    empty: useMessage('auth.security.factors.empty', 'No authenticator app is enrolled yet.'),
    loadFailed: useMessage('auth.security.factors.load_failed', 'Your authenticator apps could not be loaded.'),
    retry: useMessage('common.action.retry', 'Retry'),
    add: useMessage('auth.security.enroll.add', 'Add authenticator app'),
    unnamed: useMessage('auth.security.factor.unnamed', 'Authenticator app'),
    verified: useMessage('auth.security.factor.verified', 'Verified'),
    unfinished: useMessage('auth.security.factor.unfinished', 'Setup not finished'),
    remove: useMessage('auth.security.factor.remove', 'Remove'),
    removeNamed: useMessage('auth.security.factor.remove_named', 'Remove {{name}}', { name: '__FACTOR_NAME__' }),
    removeTitle: useMessage('auth.security.factor.remove_title', 'Remove authenticator app'),
    removeDetail: useMessage('auth.security.factor.remove_detail', 'Remove {{name}}? You will no longer be able to use it to verify your sign-in.', { name: confirming?.friendlyName ?? '' }),
    removeConfirm: useMessage('auth.security.factor.remove_confirm', 'Remove authenticator'),
    removeCancel: useMessage('auth.security.factor.remove_cancel', 'Keep authenticator'),
    removeFailed: useMessage('auth.security.factor.remove_failed', 'That authenticator could not be removed.'),
    enrollTitle: useMessage('auth.security.enroll.title', 'Set up an authenticator app'),
    name: useMessage('auth.security.enroll.name', 'Authenticator name'),
    nameHint: useMessage('auth.security.enroll.name_hint', 'Use a name that helps you recognise this device or app. Maximum 100 characters.'),
    start: useMessage('auth.security.enroll.start', 'Start setup'),
    starting: useMessage('auth.security.enroll.starting', 'Starting…'),
    startFailed: useMessage('auth.security.enroll.start_failed', 'Authenticator setup could not be started.'),
    scan: useMessage('auth.security.enroll.scan', 'Scan this code with your authenticator app.'),
    qrAlt: useMessage('auth.security.enroll.qr_alt', 'QR code for this authenticator setup'),
    manualKey: useMessage('auth.security.enroll.manual_key', 'Manual setup key'),
    code: useMessage('auth.security.enroll.code', 'Six-digit code'),
    codeHint: useMessage('auth.security.enroll.code_hint', 'Enter the current code shown by your authenticator app.'),
    verify: useMessage('auth.security.enroll.verify', 'Verify and finish'),
    verifying: useMessage('auth.security.enroll.verifying', 'Verifying…'),
    cancel: useMessage('auth.security.enroll.cancel', 'Cancel setup'),
    verifyFailed: useMessage('auth.security.enroll.verify_failed', 'That authenticator code was not accepted.'),
    cleanupFailed: useMessage('auth.security.enroll.cleanup_failed', 'Setup was closed, but the unfinished authenticator could not be removed. It remains listed below.'),
    enrolled: useMessage('auth.security.enroll.success', 'Authenticator app added.'),
    removed: useMessage('auth.security.factor.remove_success', 'Authenticator app removed.'),
    recoveryTitle: useMessage('auth.security.recovery.title', 'Recovery codes'),
    recoverySummary: useMessage('auth.security.recovery.summary', 'One-time codes let you recover this account if you lose your authenticator device.'),
    recoveryLoading: useMessage('auth.security.recovery.loading', 'Loading recovery-code status...'),
    recoveryLoadFailed: useMessage('auth.security.recovery.load_failed', 'Recovery-code status could not be loaded.'),
    recoveryNone: useMessage('auth.security.recovery.none', 'No recovery codes are set up yet.'),
    recoveryRemaining: useMessage('auth.security.recovery.remaining', '{{remaining}} of {{total}} codes remain.', { remaining: recoveryStatus?.remaining ?? 0, total: recoveryStatus?.total ?? 0 }),
    recoveryLow: useMessage('auth.security.recovery.low', 'Only {{remaining}} recovery codes remain. Verify your sign-in and replace the set soon.', { remaining: recoveryStatus?.remaining ?? 0 }),
    recoveryGenerate: useMessage('auth.security.recovery.generate', 'Create recovery codes'),
    recoveryGenerating: useMessage('auth.security.recovery.generating', 'Creating...'),
    recoveryGenerateNeedsAal2: useMessage('auth.security.recovery.generate_needs_aal2', 'Verify this session with your authenticator app before creating recovery codes.'),
    recoveryGenerateNeedsFactor: useMessage('auth.security.recovery.generate_needs_factor', 'Add and verify an authenticator app before creating recovery codes.'),
    recoveryRevealTitle: useMessage('auth.security.recovery.reveal.title', 'Save these recovery codes now'),
    recoveryRevealSummary: useMessage('auth.security.recovery.reveal.summary', 'Each code works once. Marble cannot show this set again after you close it.'),
    recoveryCodeItem: useMessage('auth.security.recovery.reveal.code', 'Recovery code'),
    recoveryCopy: useMessage('auth.security.recovery.copy', 'Copy all codes'),
    recoveryCopied: useMessage('auth.security.recovery.copied', 'Recovery codes copied.'),
    recoveryDownload: useMessage('auth.security.recovery.download', 'Download text file'),
    recoveryPrint: useMessage('auth.security.recovery.print', 'Print'),
    recoveryAcknowledge: useMessage('auth.security.recovery.acknowledge', 'I saved these codes somewhere safe'),
    recoveryCloseReveal: useMessage('auth.security.recovery.reveal.close', 'Finish'),
    recoveryMustAcknowledge: useMessage('auth.security.recovery.acknowledge_required', 'Confirm that you saved the codes before closing this one-time view.'),
    recoveryRegenerate: useMessage('auth.security.recovery.regenerate', 'Replace recovery codes'),
    recoveryRegenerateTitle: useMessage('auth.security.recovery.regenerate.title', 'Replace every recovery code?'),
    recoveryRegenerateDetail: useMessage('auth.security.recovery.regenerate.detail', 'All current recovery codes stop working immediately. A new set is shown once.'),
    recoveryRegenerateConfirm: useMessage('auth.security.recovery.regenerate.confirm', 'Replace all codes'),
    recoveryDelete: useMessage('auth.security.recovery.delete', 'Remove recovery codes'),
    recoveryDeleteTitle: useMessage('auth.security.recovery.delete.title', 'Remove the recovery path?'),
    recoveryDeleteDetail: useMessage('auth.security.recovery.delete.detail', 'Every remaining recovery code stops working immediately.'),
    recoveryDeleteConfirm: useMessage('auth.security.recovery.delete.confirm', 'Remove recovery codes'),
    recoveryKeep: useMessage('auth.security.recovery.keep', 'Keep recovery codes'),
    recoveryCode: useMessage('auth.security.recovery.verify.code', 'Recovery code'),
    recoveryCodeHint: useMessage('auth.security.recovery.verify.hint', 'Enter one saved recovery code. It is consumed after successful verification.'),
    recoveryVerify: useMessage('auth.security.recovery.verify', 'Verify with recovery code'),
    recoveryVerifying: useMessage('auth.security.recovery.verifying', 'Verifying...'),
    recoveryFailed: useMessage('auth.security.recovery.failed', 'The recovery-code action could not be completed.'),
    recoveryRemoved: useMessage('auth.security.recovery.removed', 'Recovery codes removed.'),
    recoveryVerified: useMessage('auth.security.recovery.verified', 'Recovery code accepted. This session now has two-factor assurance.'),
    copy: useMessage('auth.security.copy.action', 'Copy'),
    copied: useMessage('auth.security.copy.success', 'Copied'),
    copyFailed: useMessage('auth.security.copy.failed', 'Could not copy — select it and copy it manually'),
  }

  const local = identity.custody === 'local'
  const aal = session ? assuranceOf(session.accessToken) : null

  const loadFactors = useCallback(async () => {
    if (mounted.current) setLoadState('loading')
    try {
      const rows = await client.listMfaFactors()
      if (mounted.current) {
        setFactors(rows.filter((factor) => factor.type === 'totp'))
        setLoadState('ready')
      }
    } catch (error) {
      if (mounted.current) setLoadState('error')
    }
  // The translated fallback is deliberately not a dependency: a locale change
  // must not repeat a security read. The next failure uses the current render.
  }, [client])

  const loadRecovery = useCallback(async () => {
    if (mounted.current) setRecoveryLoadState('loading')
    try {
      const status = await client.recoveryCodeStatus()
      if (mounted.current) {
        setRecoveryStatus(status)
        setRecoveryLoadState('ready')
      }
    } catch {
      if (mounted.current) setRecoveryLoadState('error')
    }
  }, [client])

  useEffect(() => { void loadFactors(); void loadRecovery() }, [loadFactors, loadRecovery])

  const detachEnrollment = useCallback(() => {
    const active = enrollmentRef.current
    enrollmentRef.current = null
    active?.clear()
    setEnrollment(null)
    setEnrolling(false)
    setOtp('')
    return active
  }, [])

  /* A host can unmount the sheet without asking it to close (session loss,
     route change). Drop the secret synchronously and make one best-effort
     removal of the unfinished server factor. No result writes into an
     unmounted component. */
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      const active = enrollmentRef.current
      enrollmentRef.current = null
      active?.clear()
      if (active) {
        /* Never race DELETE against an in-flight verification. If verification
           succeeds, the factor is intentionally kept. If it fails, its catch
           joins this deferred cleanup and removes the unfinished factor. */
        if (verifyInFlight.current) verificationUnmountCleanup.current = active
        else void client.unenrollMfaFactor(active.factor.id).catch(() => undefined)
      }
    }
  }, [client])

  useEffect(() => () => {
    const reveal = recoveryRevealRef.current
    recoveryRevealRef.current = null
    reveal?.clear()
  }, [])

  useEffect(() => {
    if (enrolling && !enrollment && !starting) nameInput.current?.focus()
  }, [enrolling, enrollment, starting])

  useEffect(() => {
    if (enrollment && !verifying) otpInput.current?.focus()
  }, [enrollment, verifying])

  const focusFactors = useCallback(() => {
    setTimeout(() => {
      if (!mounted.current) return
      addButton.current?.focus()
      if (document.activeElement !== addButton.current) {
        factorsRoot.current?.querySelector<HTMLButtonElement>('[data-testid^="security-factor-remove-"]')?.focus()
      }
    }, 0)
  }, [])

  async function abandonEnrollment() {
    if (cleanupInFlight.current) return
    const active = detachEnrollment()
    if (!active) return
    cleanupInFlight.current = true
    setCleaning(true)
    setFailure(null)
    try {
      await client.unenrollMfaFactor(active.factor.id)
    } catch {
      if (mounted.current) setFailure(msg.cleanupFailed)
    } finally {
      if (mounted.current) {
        setCleaning(false)
        await loadFactors()
        focusFactors()
      }
      cleanupInFlight.current = false
    }
  }

  async function closeSheet() {
    if (startInFlight.current || verifyInFlight.current || cleanupInFlight.current ||
        removalInFlight.current || recoveryInFlight.current) return
    if (recoveryRevealRef.current) {
      if (!recoveryAcknowledged) {
        setFailure(msg.recoveryMustAcknowledge)
        return
      }
      recoveryRevealRef.current.clear()
      recoveryRevealRef.current = null
      setRecoveryReveal(null)
      setRecoveryAcknowledged(false)
    }
    const active = detachEnrollment()
    if (!active) { onClose(); return }
    cleanupInFlight.current = true
    setCleaning(true)
    setFailure(null)
    try {
      await client.unenrollMfaFactor(active.factor.id)
      if (mounted.current) onClose()
    } catch {
      if (mounted.current) {
        setFailure(msg.cleanupFailed)
        await loadFactors()
        focusFactors()
      }
    } finally {
      cleanupInFlight.current = false
      if (mounted.current) setCleaning(false)
    }
  }

  async function startEnrollment() {
    if (startInFlight.current) return
    const trimmed = name.trim()
    if (trimmed.length < 1 || trimmed.length > 100) {
      setFailure(msg.nameHint)
      return
    }
    startInFlight.current = true
    setStarting(true)
    setFailure(null)
    try {
      const next = await client.enrollTotp(trimmed)
      if (!mounted.current) {
        next.clear()
        await client.unenrollMfaFactor(next.factor.id).catch(() => undefined)
        return
      }
      enrollmentRef.current = next
      setEnrollment(next)
      setEnrolling(true)
      setName('')
    } catch (error) {
      if (mounted.current) setFailure(boundedFailure(error, msg.startFailed))
    } finally {
      startInFlight.current = false
      if (mounted.current) setStarting(false)
    }
  }

  async function verifyEnrollment() {
    const active = enrollmentRef.current
    if (!active || verifyInFlight.current) return
    verifyInFlight.current = true
    setVerifying(true)
    setFailure(null)
    try {
      await client.challengeAndVerifyTotp(active.factor.id, otp)
      if (!mounted.current) {
        verificationUnmountCleanup.current = null
        return
      }
      detachEnrollment()
      await loadFactors()
      if (!mounted.current) return
      setSuccess(msg.enrolled)
      focusFactors()
    } catch (error) {
      if (!mounted.current) {
        const unfinished = verificationUnmountCleanup.current
        verificationUnmountCleanup.current = null
        if (unfinished) await client.unenrollMfaFactor(unfinished.factor.id).catch(() => undefined)
        return
      }
      /* An error ends this one-time ceremony: no secret or OTP survives it.
         Best-effort cleanup is the same rule as Cancel; a failed cleanup is
         represented by the freshly loaded unfinished factor, never hidden. */
      const message = boundedFailure(error, msg.verifyFailed)
      const failed = detachEnrollment()
      let cleanupFailed = false
      if (failed) {
        try { await client.unenrollMfaFactor(failed.factor.id) }
        catch { cleanupFailed = true }
      }
      if (mounted.current) {
        setFailure(cleanupFailed ? `${message} ${msg.cleanupFailed}` : message)
        await loadFactors()
        focusFactors()
      }
    } finally {
      verifyInFlight.current = false
      if (mounted.current) setVerifying(false)
    }
  }

  async function removeFactor() {
    const factor = confirming
    if (!factor || removalInFlight.current) return
    removalInFlight.current = true
    setRemoving(true)
    setFailure(null)
    try {
      await client.unenrollMfaFactor(factor.id)
      if (mounted.current) {
        setConfirming(null)
        await loadFactors()
        if (mounted.current) {
          setSuccess(msg.removed)
          focusFactors()
        }
      }
    } catch (error) {
      if (mounted.current) {
        setFailure(boundedFailure(error, msg.removeFailed))
        setConfirming(null)
      }
    } finally {
      removalInFlight.current = false
      if (mounted.current) setRemoving(false)
    }
  }

  function holdRecoveryReveal(next: RecoveryCodeReveal) {
    const prior = recoveryRevealRef.current
    recoveryRevealRef.current = null
    prior?.clear()
    if (!mounted.current) { next.clear(); return }
    recoveryRevealRef.current = next
    setRecoveryReveal(next)
    setRecoveryAcknowledged(false)
    setRecoveryStatus({ factorId: next.factorId, total: next.total, remaining: next.total })
  }

  function finishRecoveryReveal() {
    const active = recoveryRevealRef.current
    if (!active) return
    if (!recoveryAcknowledged) {
      setFailure(msg.recoveryMustAcknowledge)
      return
    }
    recoveryRevealRef.current = null
    active.clear()
    setRecoveryReveal(null)
    setRecoveryAcknowledged(false)
    setFailure(null)
    void loadRecovery()
  }

  async function generateRecoveryCodes() {
    if (recoveryInFlight.current || recoveryRevealRef.current) return
    recoveryInFlight.current = true
    setRecoveryAction('generate')
    setFailure(null)
    try {
      holdRecoveryReveal(await client.generateRecoveryCodes())
    } catch (error) {
      if (mounted.current) setFailure(boundedFailure(error, msg.recoveryFailed))
    } finally {
      recoveryInFlight.current = false
      if (mounted.current) setRecoveryAction(null)
    }
  }

  async function regenerateRecoveryCodes() {
    if (recoveryInFlight.current || recoveryRevealRef.current) return
    recoveryInFlight.current = true
    setRecoveryAction('regenerate')
    setConfirmRecovery(null)
    setFailure(null)
    try {
      holdRecoveryReveal(await client.regenerateRecoveryCodes())
    } catch (error) {
      if (mounted.current) setFailure(boundedFailure(error, msg.recoveryFailed))
    } finally {
      recoveryInFlight.current = false
      if (mounted.current) setRecoveryAction(null)
    }
  }

  async function deleteRecoveryCodes() {
    if (recoveryInFlight.current || recoveryRevealRef.current) return
    recoveryInFlight.current = true
    setRecoveryAction('delete')
    setConfirmRecovery(null)
    setFailure(null)
    try {
      await client.deleteRecoveryCodes()
      if (mounted.current) {
        setRecoveryStatus(null)
        setRecoveryCode('')
        setSuccess(msg.recoveryRemoved)
      }
    } catch (error) {
      if (mounted.current) setFailure(boundedFailure(error, msg.recoveryFailed))
    } finally {
      recoveryInFlight.current = false
      if (mounted.current) setRecoveryAction(null)
    }
  }

  async function verifyRecoveryCode() {
    if (recoveryInFlight.current || !recoveryStatus) return
    recoveryInFlight.current = true
    setRecoveryAction('verify')
    setFailure(null)
    try {
      await client.verifyRecoveryCode(recoveryCode)
      if (mounted.current) {
        setRecoveryCode('')
        await loadRecovery()
        if (mounted.current) setSuccess(msg.recoveryVerified)
      }
    } catch (error) {
      if (mounted.current) {
        setRecoveryCode('')
        setFailure(boundedFailure(error, msg.recoveryFailed))
      }
    } finally {
      recoveryInFlight.current = false
      if (mounted.current) setRecoveryAction(null)
    }
  }

  async function copyRecoveryCodes(codes: readonly string[]) {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      if (mounted.current) setSuccess(msg.recoveryCopied)
    } catch {
      if (mounted.current) setFailure(msg.copyFailed)
    }
  }

  function downloadRecoveryCodes(codes: readonly string[]) {
    const url = URL.createObjectURL(new Blob([`${codes.join('\n')}\n`], { type: 'text/plain;charset=utf-8' }))
    try {
      const link = document.createElement('a')
      link.href = url
      link.download = 'marble-recovery-codes.txt'
      link.click()
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  async function sendReset() {
    if (!identity.email || resetInFlight.current || resetState === 'sent') return
    resetInFlight.current = true
    setResetState('sending')
    try {
      await client.requestPasswordRecovery(identity.email, recoveryRedirectTo)
      if (mounted.current) setResetState('sent')
    } catch {
      if (mounted.current) setResetState('failed')
    } finally {
      resetInFlight.current = false
    }
  }

  const material = enrollment?.reveal() ?? null
  const recoveryMaterial = recoveryReveal?.reveal() ?? null
  const hasVerifiedAuthenticator = factors.some((factor) => factor.type === 'totp' && factor.status === 'verified')
  const canVerifyWithRecovery = recoveryStatus !== null && aal !== 'aal2'
  const factorFacts = factors.map((factor) => {
    const factorName = factor.friendlyName ?? msg.unnamed
    return {
      key: factor.id,
      label: factorName,
      value: (
        <>
          <Pill tone={factor.status === 'verified' ? 'good' : 'warn'}>
            {factor.status === 'verified' ? msg.verified : msg.unfinished}
          </Pill>{' '}
          <Button size="sm" variant="danger" onClick={() => setConfirming(factor)}
            aria-label={msg.removeNamed.replace('__FACTOR_NAME__', factorName)}
            data-testid={`security-factor-remove-${factor.id}`}>{msg.remove}</Button>
        </>
      ),
    }
  })

  return (
    <Modal label={msg.title} onClose={closeSheet} variant="sheet" restoreFocusTo={restoreFocusTo}
      dismissable={!starting && !verifying && !cleaning && !removing && !recoveryAction && !recoveryReveal}
      testid="sign-in-security">
      <PageSection title={msg.title} summary={local ? msg.localSummary : msg.directorySummary}
        testId="sign-in-security-section"
        secondaryActions={<Button variant="ghost" onClick={closeSheet}
          disabled={starting || verifying || cleaning || removing}>{msg.close}</Button>}
        primaryAction={local && identity.email ? (
          <Button variant="secondary" onClick={() => { void sendReset() }}
            disabled={resetState === 'sending' || resetState === 'sent'}
            data-testid="security-reset">
            {resetState === 'sending' ? msg.resetSending : msg.reset}
          </Button>
        ) : undefined}>
        <FactList layout="rows" data-testid="security-facts" facts={[
          { key: 'email', label: msg.signedInAs, value: identity.email },
          { key: 'method', label: msg.method, value: local ? msg.password : msg.directory },
          ...(aal ? [{ key: 'aal', label: msg.session, value: aal === 'aal2' ? msg.aal2 : msg.aal1 }] : []),
        ]} />
        {resetState === 'sent' && <Notice live="status" data-testid="security-reset-status">{msg.resetSent}</Notice>}
        {resetState === 'failed' && <Notice tone="critical" live="alert" data-testid="security-reset-status">{msg.resetFailed}</Notice>}
        {failure && <Notice tone="critical" live="alert" data-testid="security-failure">{failure}</Notice>}
        <span role="status" aria-live="polite" data-testid="security-success">{success}</span>
      </PageSection>

      <div ref={factorsRoot}>
      <PageSection title={msg.factorsTitle} headingLevel={3} summary={msg.factorsSummary}
        testId="security-factors"
        primaryAction={!enrolling && !enrollment ? (
          <Button ref={addButton} variant="primary" onClick={() => { setFailure(null); setSuccess(''); setEnrolling(true) }} data-testid="security-enroll-open">
            {msg.add}
          </Button>
        ) : undefined}>
        {loadState === 'loading' && <Notice live="status" data-testid="security-factors-loading">{msg.loading}</Notice>}
        {loadState === 'error' && <ErrorState title={msg.loadFailed} retryLabel={msg.retry}
          onRetry={() => { void loadFactors() }} data-testid="security-factors-error" />}
        {loadState === 'ready' && factorFacts.length === 0 && !enrolling && !enrollment
          ? <EmptyState data-testid="security-factors-empty">{msg.empty}</EmptyState>
          : null}
        {loadState === 'ready' && factorFacts.length > 0
          ? <FactList layout="rows" framed label={msg.factorsTitle} facts={factorFacts} data-testid="security-factor-list" />
          : null}

        {enrolling && !enrollment && (
          <Surface inset padding="comfortable" data-testid="security-enroll-name-frame">
            <Field label={msg.name} hint={msg.nameHint} required>
              <Input ref={nameInput} value={name} maxLength={100} autoComplete="off"
                onChange={(event) => setName(event.target.value)} data-testid="security-enroll-name" />
            </Field>
            <Button variant="primary" loading={starting} disabled={cleaning}
              onClick={() => { void startEnrollment() }} data-testid="security-enroll-start">
              {starting ? msg.starting : msg.start}
            </Button>
            <Button variant="ghost" disabled={starting || cleaning}
                onClick={() => { setFailure(null); setName(''); setEnrolling(false); focusFactors() }}
              data-testid="security-enroll-name-cancel">{msg.cancel}</Button>
          </Surface>
        )}

        {enrollment && material && (
          <Surface inset padding="comfortable" data-testid="security-enroll-verify-frame">
            <PageSection title={msg.enrollTitle} headingLevel={3} summary={msg.scan}
              secondaryActions={<Button variant="ghost" disabled={verifying || cleaning}
                onClick={() => { void abandonEnrollment() }} data-testid="security-enroll-cancel">{msg.cancel}</Button>}
              primaryAction={<Button variant="primary" loading={verifying} disabled={!/^\d{6}$/.test(otp) || cleaning}
                onClick={() => { void verifyEnrollment() }} data-testid="security-enroll-verify">
                {verifying ? msg.verifying : msg.verify}
              </Button>}>
              <img src={qrImage(material.qrCode)} alt={msg.qrAlt} width="192" height="192"
                data-testid="security-enroll-qr" />
              <CopyField label={msg.manualKey} value={material.secret} testId="security-enroll-secret"
                copyLabel={msg.copy} copiedMessage={msg.copied} copyFailedMessage={msg.copyFailed} />
              <Field label={msg.code} hint={msg.codeHint} required>
                <Input ref={otpInput} value={otp} inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}"
                  maxLength={6} onChange={(event) => setOtp(event.target.value.replace(/\D/gu, '').slice(0, 6))}
                  data-testid="security-enroll-code" />
              </Field>
            </PageSection>
          </Surface>
        )}
      </PageSection>
      </div>

      <PageSection title={msg.recoveryTitle} headingLevel={3} summary={msg.recoverySummary}
        testId="security-recovery"
        primaryAction={recoveryReveal ? (
          <Button variant="primary" disabled={!recoveryAcknowledged}
            onClick={finishRecoveryReveal} data-testid="security-recovery-finish">
            {msg.recoveryCloseReveal}
          </Button>
        ) : !recoveryStatus && hasVerifiedAuthenticator && aal === 'aal2' ? (
          <Button variant="primary" loading={recoveryAction === 'generate'}
            onClick={() => { void generateRecoveryCodes() }} data-testid="security-recovery-generate">
            {recoveryAction === 'generate' ? msg.recoveryGenerating : msg.recoveryGenerate}
          </Button>
        ) : recoveryStatus && aal === 'aal2' ? (
          <Button variant="primary" onClick={() => setConfirmRecovery('regenerate')}
            data-testid="security-recovery-regenerate">{msg.recoveryRegenerate}</Button>
        ) : undefined}
        secondaryActions={!recoveryReveal && recoveryStatus && aal === 'aal2' ? (
          <Button variant="danger" onClick={() => setConfirmRecovery('delete')}
            data-testid="security-recovery-delete">{msg.recoveryDelete}</Button>
        ) : undefined}>
        {recoveryLoadState === 'loading' && <Notice live="status" data-testid="security-recovery-loading">{msg.recoveryLoading}</Notice>}
        {recoveryLoadState === 'error' && <ErrorState title={msg.recoveryLoadFailed} retryLabel={msg.retry}
          onRetry={() => { void loadRecovery() }} data-testid="security-recovery-error" />}
        {recoveryLoadState === 'ready' && !recoveryStatus && !recoveryReveal && (
          <EmptyState data-testid="security-recovery-empty">{msg.recoveryNone}</EmptyState>
        )}
        {recoveryLoadState === 'ready' && !recoveryStatus && !recoveryReveal && !hasVerifiedAuthenticator && (
          <Notice tone="warn" data-testid="security-recovery-needs-factor">{msg.recoveryGenerateNeedsFactor}</Notice>
        )}
        {recoveryLoadState === 'ready' && !recoveryStatus && !recoveryReveal && hasVerifiedAuthenticator && aal !== 'aal2' && (
          <Notice tone="warn" data-testid="security-recovery-needs-aal2">{msg.recoveryGenerateNeedsAal2}</Notice>
        )}
        {recoveryStatus && !recoveryReveal && (
          <>
            <Notice live="status" data-testid="security-recovery-remaining">{msg.recoveryRemaining}</Notice>
            {recoveryStatus.remaining <= 2 && <Notice tone="warn" data-testid="security-recovery-low">{msg.recoveryLow}</Notice>}
          </>
        )}
        {canVerifyWithRecovery && !recoveryReveal && (
          <Surface inset padding="comfortable" data-testid="security-recovery-verify-frame">
            <Field label={msg.recoveryCode} hint={msg.recoveryCodeHint} required>
              <Input value={recoveryCode} autoComplete="one-time-code" maxLength={128}
                onChange={(event) => setRecoveryCode(event.target.value)}
                data-testid="security-recovery-code" />
            </Field>
            <Button variant="primary" loading={recoveryAction === 'verify'} disabled={!recoveryCode.trim()}
              onClick={() => { void verifyRecoveryCode() }} data-testid="security-recovery-verify">
              {recoveryAction === 'verify' ? msg.recoveryVerifying : msg.recoveryVerify}
            </Button>
          </Surface>
        )}
        {recoveryReveal && recoveryMaterial && (
          <Surface inset padding="comfortable" data-testid="security-recovery-reveal">
            <PageSection title={msg.recoveryRevealTitle} headingLevel={3} summary={msg.recoveryRevealSummary}>
              <FactList layout="rows" framed label={msg.recoveryRevealTitle}
                facts={recoveryMaterial.codes.map((code, index) => ({
                  key: String(index), label: `${msg.recoveryCodeItem} ${index + 1}`, value: <code>{code}</code>,
                }))} data-testid="security-recovery-codes" />
              <Button variant="secondary" onClick={() => { void copyRecoveryCodes(recoveryMaterial.codes) }}
                data-testid="security-recovery-copy">{msg.recoveryCopy}</Button>
              <Button variant="secondary" onClick={() => downloadRecoveryCodes(recoveryMaterial.codes)}
                data-testid="security-recovery-download">{msg.recoveryDownload}</Button>
              <Button variant="secondary" onClick={() => window.print()}
                data-testid="security-recovery-print">{msg.recoveryPrint}</Button>
              <Choice label={msg.recoveryAcknowledge} checked={recoveryAcknowledged}
                onChange={(event) => setRecoveryAcknowledged(event.target.checked)}
                data-testid="security-recovery-acknowledge" />
            </PageSection>
          </Surface>
        )}
      </PageSection>

      {additionalSections}

      {confirming && (
        <Modal label={msg.removeTitle} onClose={() => { if (!removing) setConfirming(null) }}
          dismissable={!removing} testid="security-remove-confirm">
          <PageSection title={msg.removeTitle} summary={msg.removeDetail}
            secondaryActions={<Button variant="ghost" disabled={removing}
              onClick={() => setConfirming(null)}>{msg.removeCancel}</Button>}
            primaryAction={<Button variant="danger" loading={removing}
              onClick={() => { void removeFactor() }} data-testid="security-remove-confirm-action">
              {msg.removeConfirm}
            </Button>}>
            <span />
          </PageSection>
        </Modal>
      )}

      {confirmRecovery && (
        <Modal label={confirmRecovery === 'regenerate' ? msg.recoveryRegenerateTitle : msg.recoveryDeleteTitle}
          onClose={() => { if (!recoveryAction) setConfirmRecovery(null) }}
          dismissable={!recoveryAction} testid="security-recovery-confirm">
          <PageSection
            title={confirmRecovery === 'regenerate' ? msg.recoveryRegenerateTitle : msg.recoveryDeleteTitle}
            summary={confirmRecovery === 'regenerate' ? msg.recoveryRegenerateDetail : msg.recoveryDeleteDetail}
            secondaryActions={<Button variant="ghost" disabled={Boolean(recoveryAction)}
              onClick={() => setConfirmRecovery(null)}>{msg.recoveryKeep}</Button>}
            primaryAction={<Button variant="danger" loading={Boolean(recoveryAction)}
              onClick={() => { void (confirmRecovery === 'regenerate' ? regenerateRecoveryCodes() : deleteRecoveryCodes()) }}
              data-testid="security-recovery-confirm-action">
              {confirmRecovery === 'regenerate' ? msg.recoveryRegenerateConfirm : msg.recoveryDeleteConfirm}
            </Button>}>
            <span />
          </PageSection>
        </Modal>
      )}
    </Modal>
  )
}
